import Sigma, {Camera} from "sigma";
import { NodeSquareProgram } from "@sigma/node-square";
import { createNodeBorderProgram } from "@sigma/node-border";
import EdgeCurveProgram from "@sigma/edge-curve";
import { downloadAsImage } from "@sigma/export-image";

import Graph from "graphology";
import forceAtlas2 from "graphology-layout-forceatlas2";
import FA2Layout from "graphology-layout-forceatlas2/worker";

import {FlowDetail, GraphDataObject} from "@app/netscope/model/data-models";
import {FlowViewHost} from "@app/netscope/netscope-common-flowview/flowview-host";

import {EdgeDisplayData, NodeDisplayData, PartialButFor} from "sigma/types";
import {ClustersViewerRenderer, VisualisationDataForRenderers} from "@app/netscope/netscope-common-flowview/renderers/ClustersViewerRenderer";
import {NetscopeUtils} from "@app/netscope/netscope-utils";
import {Settings} from "sigma/settings";

export type EchartsGraph = {
  categories: NodeCategory[];
  nodes: Node[];
  links: Link[];
  flowMap: Map<string, FlowDetail[]>;
};

export type Link = {
  source: string;
  target: string;
  source_name: string;
  target_name: string;
  exchangedBytes: number;
  lineStyle: {
    color: string;
  }
  flowKey: string;
  linkObject?: FlowDetail
}

export type NodeCategory = {
  name: string;
  symbol?: string;
  symbolSize: number;
  itemStyle: {
    color: string;
    borderColor: string;
    borderWidth: number;
  }
}

export type Node = {
  name: string;
  id: string;
  type?: string;
  color?: string;
  zindex?: number;
  symbol?: string;
  x?: number;
  y?: number;
  symbolSize?: number;
  category?: string;
  nonInteractive: boolean;
  rx?: number;
  ry?: number;
}

// Type and declare internal state:
interface State {
  hoveredNode?: string;
  hoveredEdge?: string;
  // State derived from hovered node:
  hoveredNeighbors?: Set<string>;
}

let symbolSize = 20;
let borderWidth = 5;

let categories = {
  "focused": {
    "name": "focused",
    symbolSize: symbolSize,
    itemStyle: {
      color: "#fff",
      borderColor: '#e79807',
      borderWidth: borderWidth
    }
  },
  "infrastructure": {
    "name": "infrastructure",
    symbolSize: symbolSize,
    itemStyle: {
      color: "#fff",
      borderColor: '#007FCB',
      borderWidth: borderWidth
    }
  }
  ,
  "external_ip": {
    "name": "external_ip",
    symbolSize: symbolSize,
    itemStyle: {
      color: "#fff",
      borderColor: '#a0b3b3',
      borderWidth: borderWidth
    }
  }
  ,
  "unknown_ip": {
    "name": "unknown_ip",
    symbolSize: symbolSize,
    itemStyle: {
      color: "#fff",
      borderColor: '#a620e3',
      borderWidth: borderWidth
    }
  }
  ,
  "internet_ip": {
    "name": "internet_ip",
    symbolSize: symbolSize,
    itemStyle: {
      color: "#fff",
      borderColor: '#d91919',
      borderWidth: borderWidth
    }
  }
}

export function customDrawLabel(
  context: CanvasRenderingContext2D,
  data: PartialButFor<NodeDisplayData, "x" | "y" | "size" | "label" | "color">,
  settings: Settings,
): void {
    // Don't do anything special
}

function customDrawHover(
    context: CanvasRenderingContext2D,
    data: PartialButFor<NodeDisplayData, "x" | "y" | "size" | "label" | "color">,
    settings: Settings
  ): void {
    // And finally we draw the label
    customDrawLabel(context, data, settings);
  }

export class ClustersViewerRendererSigmaJS extends ClustersViewerRenderer {

  sigmaInstance: Sigma;
  sigmaGraph: Graph;
  camera: Camera;
  intervals = []

  visualisationDataToEchartGraphData = (visualisationData: VisualisationDataForRenderers) => {
    console.log("converting1");
    let result: EchartsGraph = {
      categories: [],
      nodes: [],
      links: [],
      flowMap: new Map<string, FlowDetail[]>()
    };

    // Set categories
    for (let [categoryName, categoryObject] of Object.entries(categories)) {
      result.categories.push(categoryObject);
    }
    console.log("converting2");

    // Count per node the number of links
    let numberOfLinksPerNodes = new Map<string, number>();
    for (let link of visualisationData.links) {
      for (let resourceUuid of [link.source.uuid, link.target.uuid]) {
        if (! numberOfLinksPerNodes.has(resourceUuid)) {
          numberOfLinksPerNodes.set(resourceUuid, 0);
        }
        let linkWeight = 1;
        if (link.subflows.length > 0) {
          linkWeight = link.subflows.length;
        }

        numberOfLinksPerNodes.set(resourceUuid, numberOfLinksPerNodes.get(resourceUuid) + linkWeight);
      }
    }
    console.log("converting3");

    // Set nodes
    for (let vm of visualisationData.vms) {
      let category = "unknown_ip";
      if (vm.type == "vm" || vm.type == "host") {
        category = "infrastructure";
      }
      if (vm.type == "external_ip") {
        category = "external_ip";
      }
      if (vm.type == "asn") {
        category = "internet_ip";
      }
      if (vm.type == "internet_ip") {
        category = "internet_ip";
      }
      if (visualisationData.focusedVmsUuids.indexOf(vm.uuid) !== -1) {
        category = "focused";
      }

      let safeLogOfLinks = NetscopeUtils.safeLog(numberOfLinksPerNodes.get(vm.uuid));
      if (safeLogOfLinks == 0) {
        safeLogOfLinks = 1;
      }

      let node = {
        id: vm.uuid,
        name: vm.name,
        type: vm.type,
        category: category,
        // To force predictable layouts
        x: 0,
        y: 0,
        symbolSize: 20 * safeLogOfLinks,
        initialSymbolSize: 20 * safeLogOfLinks,
        nonInteractive: vm.nonInteractive
      };

      result.nodes.push(node)
    }
    console.log("converting4");

    // Set links
    let linksMap: Map<string, Link> = new Map<string, Link>();
    result.links = [];
    for (let link of visualisationData.links) {
      let key = [link.source.uuid, link.target.uuid].sort().join("__");

      if (!linksMap.has(key)) {
        let linkObject: Link = {
          source: link.source.uuid,
          target: link.target.uuid,
          source_name: link.source.name,
          target_name: link.target.name,
          exchangedBytes: 0,
          lineStyle: {
            color: "var(--highcharts-neutral-color-100)",
          },
          flowKey: key,
          linkObject: link
        };
        linksMap.set(key, linkObject);
        result.links.push(linkObject);
      }
      let linkObject = linksMap.get(key);
      linkObject.exchangedBytes += link.getExchangedBytes();
      if (!result.flowMap.has(key)) {
        result.flowMap.set(key, []);
      }
      result.flowMap.get(key).push(link);

      if (!result.flowMap.has(link.source.uuid)) {
        result.flowMap.set(link.source.uuid, []);
      }
      result.flowMap.get(link.source.uuid).push(link);
      if (!result.flowMap.has(link.target.uuid)) {
        result.flowMap.set(link.target.uuid, []);
      }
      result.flowMap.get(link.target.uuid).push(link);
    }
    console.log("converting5 (skip)");
    // result.links = Array.from(linksMap.values());
-
    console.log("converting6");

    return result;
  }

  override async render(flowComponent: FlowViewHost) {
    this.flowComponent = flowComponent;

    let whereClause = this.generateWhereClause(flowComponent);

    this.flowComponent.datatableTabFlowsRepository = this.flowComponent.flowsRepository.clone();

    if (flowComponent.ignoredPorts.length > 0 && !flowComponent.showIgnoredPorts) {
      let portClauses = flowComponent.ignoredPorts.map((p) => ` dst_port != ${p} `);
      let andPortClauses = portClauses.join(" and ");
      let initWhereClause = `${this.flowComponent.datatableTabFlowsRepository.whereClause} and (${andPortClauses}) and (${whereClause})`;
      await this.flowComponent.datatableTabFlowsRepository.init(initWhereClause);
    };

    this.flowComponent.datatableTabFlowsRepository.setWhereClause(whereClause);
    this.flowComponent.datatableTabFlowsRepository.showFirstPage();

    // let graphData = await this.flowComponent.flowsRepository.generateGraphData();
    let graphData = await this.flowComponent.datatableTabFlowsRepository.generateGraphData();

    let textColor: string = flowComponent.isDarkMode ? "#FFF": "#000";

    console.log("rendering (checkpoint 0)");
    let visualisationData = this.computeVisualisationDataForRenderers(graphData, flowComponent);
    console.log("rendering (checkpoint 1)");
    let echartGraphData: EchartsGraph = this.visualisationDataToEchartGraphData(visualisationData);
    console.log("rendering (checkpoint 2)");

    console.log("[graph] I will build the graph");
    const graph = new Graph();
    this.sigmaGraph = graph;

    let nodeCategoriesMap = new Map<string, NodeCategory>();
    for (let category of echartGraphData.categories) {
      nodeCategoriesMap.set(category.name, category);
    }

    // let nonInteractiveNodesUuids: Set<string> = new Set();

    let nodeMap = new Map<string, Node>;
    for (let node of echartGraphData.nodes) {
      let nodeCategory = nodeCategoriesMap.get(node.category);
      nodeMap.set(node.id, node);

      let forceLabel = true;
      if (nodeCategory.name == "internet_ip") {
        forceLabel = false;
      }

      // if (node.nonInteractive) {
      //   nonInteractiveNodesUuids.add(node.id);
      // }

      node.type = "bordered";
      node.color = "#fff";
      node.zindex = 1;

      graph.addNode(node.id, {
        type: node.type,
        label: node.name,
        x: node.x,
        y: node.y,
        size: node.symbolSize,
        color: node.color,
        borderColor: nodeCategory.itemStyle.borderColor,
        borderSize: 10,
        forceLabel: forceLabel,
        object: node,
        zindex: node.zindex
      });
    }

    for (let link of echartGraphData.links) {
      let linkColor = NetscopeUtils.exchangedBytesToColorHex(link.exchangedBytes, visualisationData.minBytes, visualisationData.maxBytes);
      let size = 1.0;

      graph.addEdge(link.source, link.target, {size: size, color: linkColor, curvature: 0.1, object: link});
    }

    console.log("[render] I will render graph");
    const state: State = {};

    this.sigmaInstance = new Sigma(graph, document.getElementById("divFlowsSvg"), {
      nodeProgramClasses: {
        bordered: createNodeBorderProgram({
          borders: [
            { size: { attribute: "borderSize", defaultValue: 5, mode: "pixels" }, color: { attribute: "borderColor" } },
            { size: { fill: true }, color: { attribute: "color" } },
          ],
        }),
        square: NodeSquareProgram
      },
      defaultEdgeType: "curve",
      labelSize: 12,
      labelColor: { color: textColor },
      renderLabels: true,
      labelRenderedSizeThreshold: 0, // all labels visible
      edgeProgramClasses: {
        curve: EdgeCurveProgram,
      },
      enableEdgeEvents: true,
      autoRescale: true,
      zoomToSizeRatioFunction: () => 1,
      defaultDrawNodeHover: customDrawHover
    });

    const canvas = this.sigmaInstance.getMouseCaptor().container;

    // Node hover events
    this.sigmaInstance.on("enterNode", (event) => {
      canvas.style.cursor = "pointer";
      if (event.node) {
        state.hoveredNode = event.node;
        state.hoveredNeighbors = new Set(graph.neighbors(event.node));

        // Refresh rendering
        this.sigmaInstance.refresh({
          // We don't touch the graph data so we can skip its reindexation
          skipIndexation: true,
        });
      }
    });
    this.sigmaInstance.on("leaveNode", (event) => {
      canvas.style.cursor = "default";
      if (event.node && state.hoveredNode == event.node) {
        state.hoveredNode = undefined
        state.hoveredNeighbors = new Set();

        // Refresh rendering
        this.sigmaInstance.refresh({
          // We don't touch the graph data so we can skip its reindexation
          skipIndexation: true,
        });
      }
    });

    // State for drag'n'drop
    let draggedNode: string | null = null;
    let isDragging = false;

    // On mouse down on a node
    //  - we enable the drag mode
    //  - save in the dragged node in the state
    //  - highlight the node
    //  - disable the camera so its state is not updated
    this.sigmaInstance.on("downNode", (e) => {

      if (! flowComponent.draggableNodesEnabled) {
        return;
      }

      isDragging = true;
      draggedNode = e.node;
      graph.setNodeAttribute(draggedNode, "highlighted", true);
      graph.setNodeAttribute(draggedNode, "fixed", true);   // pin it
      if (!this.sigmaInstance.getCustomBBox())
        this.sigmaInstance.setCustomBBox(this.sigmaInstance.getBBox());
    });

    // On mouse move, if the drag mode is enabled, we change the position of the draggedNode
    this.sigmaInstance.on("moveBody", ({ event }) => {
      if (!isDragging || !draggedNode) return;

      // Get new position of node
      const pos = this.sigmaInstance.viewportToGraph(event);

      graph.setNodeAttribute(draggedNode, "x", pos.x);
      graph.setNodeAttribute(draggedNode, "y", pos.y);

      // Prevent sigma to move camera:
      event.preventSigmaDefault();
      event.original.preventDefault();
      event.original.stopPropagation();
    });

    let layoutRecomputeCount = 0;

    const handleUp = () => {
      if (draggedNode) {
        graph.removeNodeAttribute(draggedNode, "highlighted"); // drop the visual highlight
        // note: do NOT remove "fixed" — that's what keeps it where you dropped it

        fa2Layout.start();
        layoutRecomputeCount += 1;
        const currentCount = layoutRecomputeCount;

        setTimeout(() => {
          if (layoutRecomputeCount == currentCount) {
            console.log(`Stopping animation of nodes`)
            fa2Layout.stop();
          }
        }, 200);

      }
      isDragging = false;
      draggedNode = null;
    };

    this.sigmaInstance.on("upNode", handleUp);
    this.sigmaInstance.on("upStage", handleUp);


    this.sigmaInstance.on("clickNode", (event) => {

      if (flowComponent.draggableNodesEnabled) {
        return;
      }

      let node: Node = nodeMap.get(event.node);
      // @ts-ignore
      // let flowDetails = echartGraphData.flowMap.get(node.id);

      // @ts-ignore
      let title = `Flows of ${node.name}`;

      // flowComponent.showSidePanel(title, flowDetails, node.id);

      flowComponent.askShowSidePanel(title, `where src_resource_uuid = '${node.id}' or dst_resource_uuid = '${node.id}' `, node.id, graphData.flowsRepository.nodeMap.get(node.id));
      flowComponent.askRefreshAngular();
    });

    // Edge hover events
    this.sigmaInstance.on("enterEdge", (event) => {
      canvas.style.cursor = "pointer";
      // Refresh rendering
      this.sigmaInstance.refresh({
        // We don't touch the graph data so we can skip its reindexation
        skipIndexation: true,
      });
      state.hoveredEdge = event.edge;
      state.hoveredNeighbors = new Set([graph.source(event.edge), graph.target(event.edge)]);
      // Refresh rendering
      this.sigmaInstance.refresh({
        // We don't touch the graph data so we can skip its reindexation
        skipIndexation: true,
      });
    });
    this.sigmaInstance.on("leaveEdge", (event) => {
      if (state.hoveredEdge == event.edge) {
        if (state.hoveredNode == undefined) {
          canvas.style.cursor = "default";
        }
        state.hoveredEdge = undefined;
      }
      // Refresh rendering
      this.sigmaInstance.refresh({
        // We don't touch the graph data so we can skip its reindexation
        skipIndexation: true,
      });
    });
    this.sigmaInstance.on("clickEdge", (event) => {
      let nodeSourceId = graph.source(event.edge);
      let nodeTargetId = graph.target(event.edge);
      let nodeSource: Node = nodeMap.get(nodeSourceId);
      let nodeTarget: Node = nodeMap.get(nodeTargetId);

      // @ts-ignore
      let title = `Flows between ${nodeSource.name} and ${nodeTarget.name}`;
      // flowComponent.showSidePanel(title, flowDetails);
      flowComponent.askShowSidePanel(title, `where (src_resource_uuid = '${nodeSourceId}' and dst_resource_uuid = '${nodeTargetId}') or (dst_resource_uuid = '${nodeSourceId}' and src_resource_uuid = '${nodeTargetId}') `);
      flowComponent.askRefreshAngular();
    });

    let computeDensity = () => {

      let cumulatedSize = 0;
      for (let nodeId of graph.nodes()) {
        // @ts-ignore
        let size = graph.getNodeAttributes(nodeId).size;
        cumulatedSize += Math.PI * (size * size);
      }

      // @ts-ignore
      let size = this.sigmaInstance.width * this.sigmaInstance.height;

      return (100.0 * cumulatedSize) / size;
    }

    let density = computeDensity();
    // document.title = "" + density;

    // Add border through nodeReducer
    this.sigmaInstance.setSetting("nodeReducer", (node, data) => {

      const camera = this.sigmaInstance.getCamera();
      let ratio = 1.0 / camera.getState().ratio; // zoom factor

      ratio = ratio / (Math.log(density) / Math.log(10));

      // Adjust size inversely to zoom so nodes look constant on screen
      // @ts-ignore
      const fixedSize = Math.max(5, data.size * Math.min(ratio, 1));

      const res: Partial<NodeDisplayData> = {
        ...data,
        size: fixedSize
      };

      let transparentColor = "rgba(0, 0, 0, 0)";

      if(flowComponent.isDarkMode && state.hoveredNode == node) {
        // @ts-ignore
        res.labelColor = "#fff";
      }

      if (state.hoveredNode) {
        if (state.hoveredNeighbors && !state.hoveredNeighbors.has(node) && state.hoveredNode !== node) {
          res.label = "";
          res.color = transparentColor;
          // @ts-ignore
          res.borderColor = transparentColor;
        }
      } else if(state.hoveredEdge) {
        if (state.hoveredNeighbors && !state.hoveredNeighbors.has(node)) {
          res.label = "";
          res.color = transparentColor;
          // @ts-ignore
          res.borderColor = transparentColor;
        }
      } else {
      }

      return res;
    });

    this.sigmaInstance.setSetting("edgeReducer", (edge, data) => {
      const res: Partial<EdgeDisplayData> = { ...data };

      if (state.hoveredNode) {
        if (graph.extremities(edge).indexOf(state.hoveredNode) == -1) {
          res.hidden = true;
        }
      } else if(state.hoveredEdge) {
        if (state.hoveredEdge !== edge) {
          res.hidden = true;
        }
      } else {
      }

      return res;
    });

    this.camera = this.sigmaInstance.getCamera();
    let lastZoom = this.camera.getState().ratio;

    this.sigmaInstance.getCamera().on("updated", () => {
      if (this.camera.getState().ratio !== lastZoom) {
        this.sigmaInstance.refresh({
          // We don't touch the graph data so we can skip its reindexation
          skipIndexation: true,
        }); // triggers reducers again
      }
      lastZoom = this.camera.getState().ratio;
    });

    graph.forEachNode(node => {
      graph.setNodeAttribute(node, "x", Math.random());
      graph.setNodeAttribute(node, "y", Math.random());
    });


    const sensibleSettings = forceAtlas2.inferSettings(graph);

    // Tweak
    sensibleSettings.barnesHutOptimize = true;
    sensibleSettings.barnesHutTheta = 1.0;
    sensibleSettings.edgeWeightInfluence = 0;
    sensibleSettings.slowDown = 10;
    sensibleSettings.scalingRatio = 0.2;
    // sensibleSettings.linLogMode = true;
    // sensibleSettings.gravity = 0.5;
    sensibleSettings.strongGravityMode = true;

    let beforeBenchmark = Date.now();
    const positions = forceAtlas2(graph, {
      iterations: 1,
      settings: sensibleSettings
    });
    let after = Date.now();

    let timePerStep = after - beforeBenchmark;

    const fa2Layout = new FA2Layout(graph, {
      settings: sensibleSettings,
      backgroundIterations: 1
    });

    // // @ts-ignore
    // const fa2Layout = new ForceSupervisor(graph, {isNodeFixed: (_, attr) => attr.highlighted || attr.fixed,});

    fa2Layout.start();

    // let stopAnimation = graph.edges().length;
    let stopAnimation = Math.max(500 * timePerStep, 1000);
    console.log(`I will stop animation of nodes in ${stopAnimation} milliseconds`)

    setTimeout(() => {
      console.log(`Stopping animation of nodes`)
      fa2Layout.stop();
    }, stopAnimation);

    // Ensure we show the right state
    let that = this;
    let flowPlayButtonUpdateInterval = setInterval(() => {
      // Detect if user asked to stop the recomputing of positions
      if (fa2Layout.isRunning() && flowComponent.stopComputingPositionsRequested) {
        fa2Layout.stop();
        flowComponent.stopComputingPositionsRequested = false;
        flowComponent.reComputingPositionsRequested = false;
      }
      if (!fa2Layout.isRunning() && flowComponent.reComputingPositionsRequested) {
        // Detect if user asked the recomputing of positions
        fa2Layout.start();
        flowComponent.stopComputingPositionsRequested = false;
        flowComponent.reComputingPositionsRequested = false;
      }

      // Update flow computing play button
      let currentRenderer = (flowComponent.renderer === that);

      if (currentRenderer) {
        flowComponent.isComputingPositions = fa2Layout.isRunning();
      } else {
        fa2Layout.stop();
        that.destroy(flowComponent);
      }
    }, 100);

    this.intervals.push(flowPlayButtonUpdateInterval);

    // Update legend
    flowComponent.updateLegend(visualisationData);
  }

  override zoomInVisualisation = (flowComponent: FlowViewHost) => {
    this.camera.animatedZoom({duration: 600});
  }

  override zoomOutVisualisation = (flowComponent: FlowViewHost) => {
    this.camera.animatedUnzoom({duration: 600});
  }

  override recenterVisualisation = (flowComponent: FlowViewHost) => {
    this.camera.animatedReset({duration: 600});
  }

  exportAsImage = () => {
    downloadAsImage(this.sigmaInstance, {
      layers: ["edges", "nodes", "edgeLabels", "labels"],
      format: "png",
      fileName: "cluster-viewers.png",
      backgroundColor: "#fff",
      width: undefined,
      height: undefined,
      cameraState: undefined,
    });
  }

  override destroy(flowComponent: FlowViewHost) {
    super.destroy(flowComponent);
    for (let interval of this.intervals) {
      clearInterval(interval);
    }
    if (this.sigmaInstance != undefined) {
        // this.sigmaInstance.clear();
        try {
          this.sigmaInstance.clear();
          this.sigmaGraph.clear();
          this.sigmaInstance.kill();
        } catch (e) {
          console.error("There was en error cleaning sigma context");
          console.error(e);
        }
        this.sigmaInstance = undefined;
        this.sigmaGraph = undefined;
    }
  }
}

